<script setup lang="ts">
import type { BankAccount } from '@/types/UserData';

type Props = {
  bankAccount: Partial<BankAccount>;
};

const { bankAccount = {} } = defineProps<Props>();

const { modalType } = storeToRefs(useLayoutStore());
const { isMobile } = useLayout();

const isWithdraw = computed(() => {
  return modalType.value === 'withdraw';
});

const bankAccountName = computed(() => {
  return bankAccount.bank_name;
});

const bankAccountAgency = computed(() => {
  if (!bankAccount.agency) {
    return '';
  }

  return `${bankAccount.agency}${bankAccount.agency_digit ? `-${bankAccount.agency_digit}` : ''}`;
});

const bankAccountNumber = computed(() => {
  if (!bankAccount.number) {
    return '';
  }

  return `${bankAccount.number}${bankAccount.number_digit ? `-${bankAccount.number_digit}` : ''}`;
});

const bankAccountPixValue = computed(() => {
  return bankAccount.key_pix;
});

const bankAccountPixType = computed(() => {
  return bankAccount.type_pix;
});

const isPendingAccount = computed(() => {
  return (
    !bankAccountName.value ||
    !bankAccountAgency.value ||
    !bankAccountNumber.value
  );
});
</script>

<template>
  <div
    :class="[$style.bankAccountsListItem, { [$style.withdraw]: isWithdraw }]"
  >
    <span
      v-if="bankAccountPixValue"
      class="flex items-center gap-2"
      :class="$style.pixInfo"
    >
      <NuxtIcon
        class="inline-flex"
        :filled="true"
        name="payments/pix_icon"
      />
      <span>{{ $t('account.user_data.key') }}: {{ bankAccountPixValue }}</span>
      <template v-if="bankAccountPixType">
        <span :class="[$style.pill, $style.pixPill]">
          {{ bankAccountPixType }}
        </span>
      </template>
      <span
        v-if="isPendingAccount && !isWithdraw"
        v-tooltip.bottom.left="
          $t('account.user_data.pending_confirmation_tooltip')
        "
        :class="[$style.pill, $style.pendingPill]"
      >
        {{ $t('account.user_data.pending_confirmation') }}
        {{ !isMobile ? $t('account.user_data.pending_confirmation2') : '' }}
        <NuxtIcon
          :filled="true"
          name="help"
        />
      </span>
    </span>

    <div
      v-if="isWithdraw"
      :class="$style.divider"
    />

    <template v-if="!isPendingAccount">
      <span
        v-if="!isWithdraw"
        class="opacity-30"
      >
        |
      </span>
      <span>
        <b>{{ bankAccountName }}</b>
      </span>
      <span class="opacity-30"> | </span>

      <span>{{ $t('account.user_data.account_agency') }}:</span>
      <span>{{ bankAccountAgency }}</span>
      <span class="opacity-30"> | </span>

      <span>{{ $t('account.user_data.account_number') }}:</span>
      <span>{{ bankAccountNumber }}</span>
    </template>

    <span
      v-if="isWithdraw && isPendingAccount"
      :class="$style.selectToValidate"
    >
      {{ $t('account.user_data.select_to_validate') }}
    </span>
  </div>
</template>

<style module lang="postcss">
.bankAccountsListItem {
  @apply flex items-center gap-2 flex-wrap py-3.5 px-1.5 w-full whitespace-nowrap;

  .pixInfo {
    @apply flex-wrap;
  }

  &.withdraw {
    @apply py-2 gap-y-0;

    span:not(.pixInfo) {
      @apply text-2xs opacity-50;
    }

    .pixInfo {
      span {
        @apply opacity-100;
      }

      span:not(.pill) {
        @apply text-sm;
      }
    }

    span.selectToValidate {
      @apply text-[.6rem] text-error opacity-90 leading-3 mt-0.5 whitespace-normal;
    }
  }
}

.pill {
  @apply px-1.5 py-0.5 rounded-full text-[.6rem] font-bold leading-3 mr-auto relative flex items-center gap-1;

  &.pixPill {
    @apply bg-primary/10 text-primary;
  }

  &.pendingPill {
    @apply bg-error/10 text-error;
  }
}

.divider {
  @apply w-full h-px;
}
</style>
